import { Cpu, Menu, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface NavLink {
  name: string;
  href: string;
}

const NAV_LINKS: NavLink[] = [
  { name: 'Financial', href: '#financial' },
  { name: 'Healthcare', href: '#healthcare' },
  { name: 'Retail', href: '#retail' },
  { name: 'Travel', href: '#travel' },
  { name: 'Professional', href: '#professional' },
];

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollTo = useCallback((id: string) => {
    setIsOpen(false);
    const element = document.getElementById(id.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleLogoClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleToggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-md shadow-md py-3' : 'bg-transparent py-5'
      }`}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <button
            type="button"
            className="flex items-center cursor-pointer bg-transparent border-none"
            onClick={handleLogoClick}
            aria-label="Go to homepage"
          >
            <div className="bg-brand-600 p-2 rounded-lg mr-3">
              <Cpu className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-900">OmniFlow</span>
          </button>

          <div className="hidden md:flex space-x-8">
            {NAV_LINKS.map((link) => (
              <button
                type="button"
                key={link.name}
                onClick={() => handleScrollTo(link.href)}
                className="text-slate-600 hover:text-brand-600 font-medium transition-colors bg-transparent border-none cursor-pointer"
              >
                {link.name}
              </button>
            ))}
            <button
              type="button"
              className="bg-brand-600 text-white px-5 py-2 rounded-full font-medium hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/30 cursor-pointer"
            >
              Get Started
            </button>
          </div>

          <div className="md:hidden">
            <button
              type="button"
              onClick={handleToggleMenu}
              className="text-slate-600 hover:text-brand-600 bg-transparent border-none cursor-pointer p-2"
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div
          id="mobile-menu"
          className="md:hidden bg-white border-b border-slate-100 absolute w-full"
          role="menu"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {NAV_LINKS.map((link) => (
              <button
                type="button"
                key={link.name}
                onClick={() => handleScrollTo(link.href)}
                className="block w-full text-left px-3 py-2 text-base font-medium text-slate-600 hover:text-brand-600 hover:bg-slate-50 rounded-md bg-transparent border-none cursor-pointer"
                role="menuitem"
              >
                {link.name}
              </button>
            ))}
            <button
              type="button"
              className="w-full mt-4 bg-brand-600 text-white px-5 py-3 rounded-lg font-medium border-none cursor-pointer"
            >
              Get Started
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
